import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';

/**
 * MealCataloguePicker
 * ===================
 * Modal that lets staff browse the platform-wide catalogue of common UK
 * childcare / school-club meals, search/filter, then clone an item into
 * their own meal library with one click.
 *
 * Props:
 *   open          : boolean
 *   onClose       : () => void
 *   onCloned      : (meal) => void  - parent should refresh meal list
 *
 * UX choices:
 *   - Search box debounced 200ms (catalogue is in-memory on the server,
 *     so it's basically instant).
 *   - Category chips (Breakfast / Lunch / Snack / Tea / Drink) for quick
 *     filtering — preselected to "All" on open.
 *   - Each card shows name, description, allergen badges, suggested
 *     slots, and a "Use this" button that POSTs /api/meals/from-catalogue.
 *   - Clicking a card before clicking "Use this" opens a small inline
 *     edit panel where the user can rename or strip allergens before
 *     saving — without leaving the modal.
 */
const CATEGORIES = [
  { value: '', label: 'All' },
  { value: 'breakfast', label: '🥣 Breakfast' },
  { value: 'lunch', label: '🍽️ Lunch' },
  { value: 'snack', label: '🍎 Snack' },
  { value: 'tea', label: '🥪 Tea' },
  { value: 'drink', label: '🥛 Drink' },
];

export function MealCataloguePicker({ open, onClose, onCloned }) {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeItem, setActiveItem] = useState(null);
  const [overrideName, setOverrideName] = useState('');
  const [overrideAllergens, setOverrideAllergens] = useState([]);
  const [cloning, setCloning] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.getMealCatalogue({ search, category });
      setItems(r.items || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [search, category]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(reload, 200);
    return () => clearTimeout(t);
  }, [open, reload]);

  useEffect(() => {
    // Reset transient state when the modal closes.
    if (!open) {
      setActiveItem(null);
      setOverrideName('');
      setOverrideAllergens([]);
    }
  }, [open]);

  const startEdit = (item) => {
    setActiveItem(item);
    setOverrideName(item.name);
    setOverrideAllergens([...(item.allergens || [])]);
  };

  const toggleAllergen = (a) => {
    setOverrideAllergens((prev) => prev.includes(a) ? prev.filter(x => x !== a) : [...prev, a]);
  };

  const handleClone = async () => {
    if (!activeItem) return;
    setCloning(true);
    try {
      const meal = await api.cloneMealFromCatalogue({
        catalogue_id: activeItem.catalogue_id,
        name_override: overrideName.trim() || undefined,
        allergens_override: overrideAllergens,
      });
      toast.success(`"${meal.name}" added to your meal library`, {
        description: 'You can edit it any time from the Meal Library tab.',
      });
      onCloned?.(meal);
      onClose?.();
    } catch (err) {
      toast.error('Could not add to library', { description: err.message });
    } finally {
      setCloning(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="meal-catalogue-picker">
      <div
        className="modal-content"
        style={{ maxWidth: 900, maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid hsl(var(--border))' }}>
          <h2 style={{ margin: 0 }}>📚 Pick from meal library</h2>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>
            Common UK childcare & school-club meals with allergens pre-tagged. Pick one to add it to your library — you can tweak it after.
          </p>
        </div>

        <div style={{ padding: '0.75rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', borderBottom: '1px solid hsl(var(--border))' }}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, description or ingredient (e.g. 'pasta', 'chicken', 'gluten')"
            data-testid="catalogue-search"
            style={{ padding: '0.5rem 0.75rem', borderRadius: 4, border: '1px solid hsl(var(--border))' }}
            autoFocus
          />
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            {CATEGORIES.map((c) => (
              <button
                key={c.value || 'all'}
                type="button"
                onClick={() => setCategory(c.value)}
                data-testid={`catalogue-cat-${c.value || 'all'}`}
                className={category === c.value ? 'btn btn-primary' : 'btn btn-ghost'}
                style={{ fontSize: '0.8rem', padding: '0.3rem 0.7rem' }}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem 1.25rem' }}>
          {error && <div className="error-message">{error}</div>}
          {loading && <p style={{ color: 'hsl(var(--muted-foreground))' }}>Loading catalogue…</p>}
          {!loading && items.length === 0 && (
            <p style={{ color: 'hsl(var(--muted-foreground))' }}>No catalogue items match your filters.</p>
          )}
          {!loading && items.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '0.75rem' }}>
              {items.map((item) => {
                const isActive = activeItem?.catalogue_id === item.catalogue_id;
                return (
                  <div
                    key={item.catalogue_id}
                    data-testid={`catalogue-item-${item.catalogue_id}`}
                    style={{
                      border: `1px solid ${isActive ? 'hsl(var(--primary))' : 'hsl(var(--border))'}`,
                      background: isActive ? 'hsl(var(--primary)/0.06)' : 'hsl(var(--card))',
                      borderRadius: 6,
                      padding: '0.75rem',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.4rem',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                      <strong style={{ fontSize: '0.92rem' }}>{item.name}</strong>
                      <span style={{ fontSize: '0.7rem', padding: '0.05rem 0.4rem', borderRadius: 3, background: 'hsl(var(--muted))', color: 'hsl(var(--muted-foreground))' }}>
                        {item.category}
                      </span>
                    </div>
                    {item.description && (
                      <small style={{ color: 'hsl(var(--muted-foreground))', lineHeight: 1.4 }}>{item.description}</small>
                    )}
                    {(item.allergens || []).length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {item.allergens.map((a) => (
                          <span key={a} style={{ fontSize: '0.65rem', padding: '0.05rem 0.4rem', borderRadius: 3, background: 'hsl(0 70% 92%)', color: 'hsl(0 70% 35%)', border: '1px solid hsl(0 70% 80%)' }}>
                            ⚠ {a}
                          </span>
                        ))}
                      </div>
                    )}
                    {(item.dietary_tags || []).length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {item.dietary_tags.slice(0, 4).map((t) => (
                          <span key={t} style={{ fontSize: '0.65rem', padding: '0.05rem 0.4rem', borderRadius: 3, background: 'hsl(140 50% 92%)', color: 'hsl(140 50% 25%)' }}>
                            {t.replace(/-/g, ' ')}
                          </span>
                        ))}
                      </div>
                    )}
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => startEdit(item)}
                      data-testid={`catalogue-pick-${item.catalogue_id}`}
                      style={{ fontSize: '0.78rem', alignSelf: 'flex-start', padding: '0.25rem 0.6rem', marginTop: 'auto' }}
                    >
                      {isActive ? '✓ Selected' : 'Use this →'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {activeItem && (
          <div style={{ padding: '0.85rem 1.25rem', borderTop: '1px solid hsl(var(--border))', background: 'hsl(var(--muted)/0.5)' }} data-testid="catalogue-edit-panel">
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600 }}>Name in your library</label>
                <input
                  value={overrideName}
                  onChange={(e) => setOverrideName(e.target.value)}
                  data-testid="catalogue-override-name"
                  style={{ width: '100%', padding: '0.4rem 0.6rem', borderRadius: 4, border: '1px solid hsl(var(--border))', marginTop: 4 }}
                />
              </div>
              <div style={{ flex: 2, minWidth: 320 }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600 }}>Allergens (click to toggle)</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                  {(activeItem.allergens || []).map((a) => {
                    const on = overrideAllergens.includes(a);
                    return (
                      <button key={a} type="button" onClick={() => toggleAllergen(a)}
                        data-testid={`catalogue-override-allergen-${a}`}
                        style={{
                          fontSize: '0.7rem', padding: '0.15rem 0.5rem', borderRadius: 3,
                          border: '1px solid', cursor: 'pointer',
                          background: on ? 'hsl(0 70% 50%)' : 'hsl(var(--card))',
                          color: on ? 'white' : 'hsl(var(--foreground))',
                          borderColor: on ? 'hsl(0 70% 50%)' : 'hsl(var(--border))',
                        }}
                      >
                        {on ? '✓ ' : ''}{a}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div style={{ marginTop: '0.85rem', display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-ghost" onClick={() => setActiveItem(null)} data-testid="catalogue-cancel-pick">
                Pick another
              </button>
              <button type="button" className="btn btn-primary" onClick={handleClone} disabled={cloning} data-testid="catalogue-confirm-clone">
                {cloning ? 'Adding…' : `Add "${overrideName.trim() || activeItem.name}" to library`}
              </button>
            </div>
          </div>
        )}

        <div style={{ padding: '0.65rem 1.25rem', borderTop: '1px solid hsl(var(--border))', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <small style={{ color: 'hsl(var(--muted-foreground))' }}>{items.length} meal{items.length === 1 ? '' : 's'} matching</small>
          <button type="button" className="btn btn-ghost" onClick={onClose} data-testid="catalogue-close">Close</button>
        </div>
      </div>
    </div>
  );
}


/**
 * AllergenSuggestPanel
 * ====================
 * Inline panel that lives inside the Meal Library Add/Edit modal. As the
 * user types the meal name (debounced 700ms) we call the AI assistant
 * and render the suggested allergens + dietary tags as click-to-accept
 * chips.
 *
 * Props:
 *   name          : current meal name (controlled by parent form)
 *   description   : current description
 *   currentAllergens : array of currently-selected allergen codes
 *   currentDietaryTags : array of currently-selected dietary tags
 *   onApply       : ({ allergens, dietary_tags }) => void
 *                   Called when the user accepts one or more suggestions.
 *
 * UX guarantees:
 *   - Auto-trigger is debounced and only fires when name length >= 3.
 *   - Manual "✨ Suggest now" button bypasses debounce.
 *   - Heuristic-only results are clearly badged so staff don't blindly
 *     trust them.
 *   - Already-selected allergens are not re-suggested.
 */
export function AllergenSuggestPanel({
  name,
  description,
  currentAllergens = [],
  currentDietaryTags = [],
  onApply,
}) {
  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastQueriedName, setLastQueriedName] = useState('');

  const fetchSuggestion = useCallback(async (n, d) => {
    if (!n || n.trim().length < 3) {
      setSuggestion(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await api.suggestMealAllergens({ name: n, description: d || '' });
      setSuggestion(r);
      setLastQueriedName(n);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced auto-trigger.
  useEffect(() => {
    const t = setTimeout(() => {
      if (name && name !== lastQueriedName) {
        fetchSuggestion(name, description);
      }
    }, 700);
    return () => clearTimeout(t);
  }, [name, description, fetchSuggestion, lastQueriedName]);

  const newAllergens = (suggestion?.allergens || []).filter(a => !currentAllergens.includes(a));
  const newTags = (suggestion?.dietary_tags || []).filter(t => !currentDietaryTags.includes(t));
  const hasNewSuggestions = newAllergens.length > 0 || newTags.length > 0;

  const acceptAll = () => {
    onApply?.({
      allergens: Array.from(new Set([...currentAllergens, ...newAllergens])),
      dietary_tags: Array.from(new Set([...currentDietaryTags, ...newTags])),
    });
  };
  const acceptOne = (kind, value) => {
    if (kind === 'allergen') {
      onApply?.({
        allergens: Array.from(new Set([...currentAllergens, value])),
        dietary_tags: currentDietaryTags,
      });
    } else {
      onApply?.({
        allergens: currentAllergens,
        dietary_tags: Array.from(new Set([...currentDietaryTags, value])),
      });
    }
  };

  const isHeuristic = suggestion?.source === 'heuristic' || suggestion?.source === 'heuristic_rate_limited';

  return (
    <div
      data-testid="ai-allergen-panel"
      style={{
        marginTop: '0.5rem',
        padding: '0.75rem',
        background: 'hsl(220 60% 97%)',
        border: '1px solid hsl(220 50% 88%)',
        borderRadius: 6,
        fontSize: '0.85rem',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <strong>✨ AI allergen assistant</strong>
        <button
          type="button"
          onClick={() => fetchSuggestion(name, description)}
          disabled={loading || !name || name.trim().length < 3}
          data-testid="ai-allergen-refresh"
          className="btn btn-ghost"
          style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}
          title="Re-run suggestions"
        >
          {loading ? '…' : '🔄 Refresh'}
        </button>
      </div>

      {!name || name.trim().length < 3 ? (
        <small style={{ color: 'hsl(var(--muted-foreground))' }}>
          Start typing the meal name and we'll suggest UK14 allergens automatically.
        </small>
      ) : loading ? (
        <small style={{ color: 'hsl(var(--muted-foreground))' }}>Thinking…</small>
      ) : error ? (
        <small style={{ color: 'hsl(var(--destructive))' }}>Couldn't fetch suggestions: {error}</small>
      ) : suggestion ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{
              fontSize: '0.7rem', padding: '0.05rem 0.4rem', borderRadius: 999,
              background: suggestion.confidence === 'high' ? 'hsl(140 50% 88%)' :
                          suggestion.confidence === 'medium' ? 'hsl(45 95% 88%)' : 'hsl(var(--muted))',
              color: suggestion.confidence === 'high' ? 'hsl(140 50% 25%)' :
                     suggestion.confidence === 'medium' ? 'hsl(35 80% 30%)' : 'hsl(var(--muted-foreground))',
            }}>
              {suggestion.confidence} confidence
            </span>
            {isHeuristic && (
              <span style={{ fontSize: '0.65rem', color: 'hsl(var(--muted-foreground))' }}>
                offline heuristic — please double-check
              </span>
            )}
          </div>

          {suggestion.safety_note && (
            <div style={{ fontSize: '0.78rem', color: 'hsl(var(--foreground))', marginBottom: 6, fontStyle: 'italic' }}>
              {suggestion.safety_note}
            </div>
          )}

          {!hasNewSuggestions ? (
            <small style={{ color: 'hsl(140 50% 30%)' }}>✓ Your current selections look complete.</small>
          ) : (
            <>
              {newAllergens.length > 0 && (
                <div style={{ marginBottom: 6 }}>
                  <small style={{ display: 'block', marginBottom: 3, color: 'hsl(var(--muted-foreground))' }}>Suggested allergens (click to add):</small>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {newAllergens.map((a) => (
                      <button
                        key={a}
                        type="button"
                        onClick={() => acceptOne('allergen', a)}
                        data-testid={`ai-suggest-allergen-${a}`}
                        style={{
                          fontSize: '0.72rem', padding: '0.15rem 0.5rem', borderRadius: 3,
                          border: '1px dashed hsl(0 70% 60%)', background: 'hsl(0 60% 97%)',
                          color: 'hsl(0 70% 35%)', cursor: 'pointer',
                        }}
                      >
                        + {a}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {newTags.length > 0 && (
                <div style={{ marginBottom: 6 }}>
                  <small style={{ display: 'block', marginBottom: 3, color: 'hsl(var(--muted-foreground))' }}>Suggested dietary tags:</small>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {newTags.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => acceptOne('tag', t)}
                        data-testid={`ai-suggest-tag-${t}`}
                        style={{
                          fontSize: '0.72rem', padding: '0.15rem 0.5rem', borderRadius: 3,
                          border: '1px dashed hsl(140 50% 50%)', background: 'hsl(140 50% 97%)',
                          color: 'hsl(140 50% 25%)', cursor: 'pointer',
                        }}
                      >
                        + {t.replace(/-/g, ' ')}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <button
                type="button"
                onClick={acceptAll}
                data-testid="ai-suggest-accept-all"
                className="btn btn-primary"
                style={{ fontSize: '0.78rem', padding: '0.25rem 0.6rem', marginTop: 4 }}
              >
                Accept all suggestions
              </button>
            </>
          )}
        </>
      ) : (
        <small style={{ color: 'hsl(var(--muted-foreground))' }}>Click 🔄 Refresh to fetch suggestions.</small>
      )}
    </div>
  );
}
